import * as Sentry from '@sentry/react';

import { formatEngagementMoney, formatHours } from '../money';
import type { AmendmentView } from '../viewModel';

export interface FieldEntry {
  label: string;
  /** What this amendment sets. */
  next: string;
  /** The value immediately prior, or `—` when there is no snapshot yet. */
  prior: string;
}

/**
 * The subset of `changeSet` keys that survive redaction for BOTH sides.
 *
 * The api writes exactly three keys (`amendment.go:104-108`) and rebuilds the
 * blob per viewer from a key allowlist: the expert keeps `expert_rate`, the
 * customer never sees it. So a rate-only amendment reaches the customer as an
 * empty object — which is why rate deltas are read from the typed per-side pair
 * fields instead, and this shape covers only what is genuinely shared.
 */
interface SharedChangeSet {
  nte_hours_total?: string;
  scope?: string;
}

/**
 * A blob the client can't read is a contract problem nothing else would
 * surface, so it must be a real, searchable Sentry event: a breadcrumb only
 * ships attached to some LATER error, and the point here is that nothing
 * errors — the UI degrades to "no entries" (the ENG-5528 precedent in
 * `messaging/utils.ts`). Reported once per amendment/field per session, and
 * never with blob content — scope text is customer data.
 */
const reportedBlobs = new Set<string>();

/** Exported for tests only — the module-level dedup would otherwise leak between cases. */
export function resetUnreadableBlobReports(): void {
  reportedBlobs.clear();
}

function reportUnreadableBlob(
  amendmentId: string,
  field: string,
  reason: 'unparseable' | 'wrong_shape'
): void {
  const key = `${amendmentId}:${field}`;
  if (reportedBlobs.has(key)) return;
  reportedBlobs.add(key);
  Sentry.captureMessage('Amendment blob the client cannot read', {
    level: 'warning',
    tags: { area: 'engagement', reason },
    extra: { amendmentId, field },
  });
}

/**
 * Narrow the two keys this module reads, dropping — and reporting — any that
 * arrive wrong-typed.
 *
 * The object check above proves only the top level: a well-formed blob whose
 * `scope` arrived as an object would reach `FieldEntry.next` (typed `string`)
 * and take the tab down with "Objects are not valid as a React child" — the
 * crash the guarded parse exists to prevent, and as a crash rather than as the
 * `wrong_shape` event it really is. Hours are the api's fixed two-decimal
 * string ("120.00", `pricing.go:139`), so a non-numeric one is the same
 * contract problem. Defensive today; the blob is untrusted everywhere else in
 * this module, and casting one level down was the one step that wasn't.
 */
function shapeBlob(
  raw: Record<string, unknown>,
  amendmentId: string,
  field: string
): SharedChangeSet {
  const shaped: SharedChangeSet = {};
  const hours = raw.nte_hours_total;
  const scope = raw.scope;

  if (hours !== undefined) {
    if (typeof hours === 'string' && hours.trim() !== '' && Number.isFinite(Number(hours))) {
      shaped.nte_hours_total = hours;
    } else {
      reportUnreadableBlob(amendmentId, field, 'wrong_shape');
    }
  }

  if (scope !== undefined) {
    if (typeof scope === 'string') {
      shaped.scope = scope;
    } else {
      reportUnreadableBlob(amendmentId, field, 'wrong_shape');
    }
  }

  return shaped;
}

/**
 * `changeSet` and `priorVersionSnapshot` are free-form JSON strings on the wire,
 * viewer-redacted server-side. Parsing is guarded because a malformed blob must
 * degrade to "no entries" rather than take down the tab — silently only to the
 * user: the unreadable blob is captured above.
 */
function parseBlob(raw: string | null, amendmentId: string, field: string): SharedChangeSet {
  if (raw === null || raw === '') return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      // Valid JSON, wrong shape — `'null'`, an array — is the same contract
      // problem as unparseable text.
      reportUnreadableBlob(amendmentId, field, 'wrong_shape');
      return {};
    }
    return shapeBlob(parsed as Record<string, unknown>, amendmentId, field);
  } catch {
    reportUnreadableBlob(amendmentId, field, 'unparseable');
    return {};
  }
}

/**
 * The viewer-shared keys of an amendment's change set, parsed with the same
 * guard as rendering. Used to prefill the counter form: a counter SUPERSEDES
 * the proposal it answers, so it starts from the proposal's own values —
 * an empty form would silently drop every part the counterer didn't retype.
 */
export function sharedChangeSetOf(amendment: AmendmentView): SharedChangeSet {
  return parseBlob(amendment.changeSet, amendment.id, 'changeSet');
}

function rateText(cents: number, currency: string | null): string {
  return `${formatEngagementMoney(cents, currency)}/hr`;
}

/**
 * Hours are a fixed two-decimal string on the wire ("120.00"); `formatHours` is
 * what every other live surface renders them with, so an amendment reads "120h"
 * like the Overview term it changes rather than "120.00 hours".
 */
function hoursText(wire: string): string {
  return formatHours(Number(wire));
}

/**
 * Flatten an amendment into display entries, in a stable field order.
 *
 * Two rules, both §6.3:
 *
 * 1. **Rate deltas come from the typed pair fields, never the blob.** The
 *    resolvers null the wrong side's pair, so reading `newExpertRateCents` for a
 *    customer viewer yields nothing rather than a number the customer must not
 *    see. Parsing a rate out of `changeSet` would work today only because the
 *    server happens to strip the key — that is the server's invariant to keep,
 *    not one to depend on twice.
 * 2. **A null field hides its block.** Never a 0, never "Unknown", never an
 *    error — a denied value is a view decision.
 */
export function fieldEntriesLive(amendment: AmendmentView, currency: string | null): FieldEntry[] {
  const change = parseBlob(amendment.changeSet, amendment.id, 'changeSet');
  const prior = parseBlob(amendment.priorVersionSnapshot, amendment.id, 'priorVersionSnapshot');
  const entries: FieldEntry[] = [];

  if (change.nte_hours_total !== undefined) {
    entries.push({
      label: 'NTE hours',
      next: hoursText(change.nte_hours_total),
      prior: prior.nte_hours_total !== undefined ? hoursText(prior.nte_hours_total) : '—',
    });
  }

  if (change.scope !== undefined) {
    entries.push({
      label: 'Scope',
      next: change.scope,
      // `priorVersionSnapshot` is keyed by engagement COLUMN
      // (`amendment.go:176-184`) and scope has no column — it is an audit record
      // only. So this reference is a dash today, by construction rather than by
      // omission; the read stays for the day a column exists.
      prior: prior.scope ?? '—',
    });
  }

  // Exactly one side resolves; the other is null and contributes nothing.
  const rate =
    amendment.newExpertRateCents !== null
      ? {
          label: 'Your rate',
          next: amendment.newExpertRateCents,
          was: amendment.priorExpertRateCents,
        }
      : amendment.newCustomerRateLoadedCents !== null
        ? {
            // `OverviewPanel`'s term for the same number: the customer's own
            // loaded rate, which only customer-side viewers ever receive. Kept
            // in step with it deliberately — ENG-5731 relabelled both, because
            // the figure is the not-to-exceed total over the hours and no
            // individual hour is billed at it. Letting the two drift would also
            // kill the §6.3 label guard in `amendments-read.test.tsx`, which
            // catches a customer row reaching an expert by this exact string.
            label: 'Blended rate',
            next: amendment.newCustomerRateLoadedCents,
            was: amendment.priorCustomerRateLoadedCents,
          }
        : null;

  if (rate !== null) {
    entries.push({
      label: rate.label,
      next: rateText(rate.next, currency),
      prior: rate.was !== null ? rateText(rate.was, currency) : '—',
    });
  }

  return entries;
}
